<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useTableStore } from '@/stores/tableStore.ts'
import Backend from '@/models/backend.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'

/**
 * Stores / models
 */
const tableStore = useTableStore()
const shipActionStore = useShipActionStore()
const gameStore = useGameStore()
const mapStore = useMapStore()

/**
 * Variables
 */
const isOpen = ref(false)
const menuRef = ref(null)

/**
 * Functions
 */
function toggleMenu() {
  isOpen.value = !isOpen.value
}

// Perform actions on the map with selected shipsshipActionData.data
const actionMap = () => {
  if (tableStore.mapFleetShips.selectedRowIds.length > 0) {
    shipActionStore.initShipsFromIds(tableStore.mapFleetShips.selectedRowIds)
    tableStore.mapFleetShips.selectedRowIds = []
    tableStore.mapFleetShips.selectedAll = false
    gameStore.modalType = ''
  }
}

/**
 * Attach ships to a fleet
 */
const attachToFleet = () => {
  if (tableStore.mapFleetShips.selectedRowIds.length > 0) {
    gameStore.modalType = 'assignToFleet'
    gameStore.modalData = tableStore.mapFleetShips.selectedRowIds
    tableStore.mapFleetShips.selectedRowIds = []
    tableStore.mapFleetShips.selectedAll = false
  }
}

/**
 * Detach ships from a fleet
 */
const detachFromFleet = () => {
  if (tableStore.mapFleetShips.selectedRowIds.length > 0) {
    const backend = new Backend()
    backend.detachShipsFromFleet(tableStore.mapFleetShips.selectedRowIds)
    tableStore.mapFleetShips.selectedRowIds = []
    tableStore.mapFleetShips.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

function closeMenu() {
  isOpen.value = false
}

function handleClickOutside(event) {
  if (!menuRef.value.contains(event.target)) {
    closeMenu()
  }
}

const destroyFleet = () => {
  if (tableStore.mapFleetShips.selectedRowIds.length > 0) {
    gameStore.modal2Type = 'confirmationDelete'
    gameStore.modal2Data = {
      title: 'Are you sure?',
      message: 'Are you sure you want to destroy these ships?',
      actionText: 'Yes',
      cancelText: 'No',
      action: () => {
        const backend = new Backend()
        backend.destroyShips(tableStore.mapFleetShips.selectedRowIds)
        tableStore.mapFleetShips.selectedRowIds = []
        tableStore.mapFleetShips.selectedAll = false
      }
    }
  }
}

/**
 * Lifecycle hooks
 */
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="relative" @click.stop="toggleMenu">
    <!-- Icon Button -->
    <ActionButton class="my-1"> Action </ActionButton>
    <!-- Dropdown Menu -->
    <div
      v-show="isOpen"
      ref="menuRef"
      class="absolute w-52 border border-slate-500 bg-slate-800 rounded z-50 mt-1"
    >
      <div
        class="p-2 flex items-center text-slate-300 hover:bg-slate-700 cursor-pointer"
        @click="actionMap"
      >
        <GenericIcon type="cmd_decouple" class="mr-3" />
        <div>Detach & Command</div>
      </div>
      <div
        class="p-2 flex items-center text-red-500 hover:bg-slate-700 cursor-pointer"
        @click="destroyFleet"
      >
        <GenericIcon type="trash" class="mr-3" />
        <div>Destroy Ships</div>
      </div>
      <div
        class="p-2 flex items-center text-slate-300 hover:bg-slate-700 cursor-pointer"
        @click="attachToFleet"
      >
        <GenericIcon type="add" class="mr-3" />
        <div>Attach to another Fleet</div>
      </div>
      <div
        class="p-2 flex items-center text-slate-300 hover:bg-slate-700 cursor-pointer"
        @click="detachFromFleet"
      >
        <GenericIcon type="remove" class="mr-3" />
        <div>Detach from Fleet</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Add any specific styles for your dropdown here */
</style>
